/** @jsxImportSource @emotion/react */
import React, { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { ControlFilldBG, GrayColor, PrimaryColor } from 'ui/theme/Color';

type Corner = 'tl' | 'tr' | 'bl' | 'br';

const STORAGE_KEY = 'chatbotButton.corner';
const DRAG_THRESHOLD = 4;

const loadCorner = (): Corner => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'tl' || saved === 'tr' || saved === 'bl' || saved === 'br' ? saved : 'br';
};

interface ChatbotButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string; // 버튼 문구
  compact?: boolean; // true인 경우 버튼명 없이 아이콘 + 화살표만 노출
  floating?: boolean; // 플로팅버튼 여부
  draggable?: boolean; // 드래그가능여부 (기존화면 버튼 가리는 경우 사용하기위해 추가)
}

/**
 * @desc 챗봇 버튼
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.23   jdseong       신규 생성
 */
export const ChatbotButton = ({
  label,
  compact = false,
  floating = true,
  draggable = true,
  className,
  onClick,
  ...props
}: ChatbotButtonProps) => {
  const { t } = useTranslation();
  const nodeRef = useRef<HTMLButtonElement>(null);
  const draggedRef = useRef(false);
  const [corner, setCorner] = useState<Corner>(loadCorner);

  const text = label || String(t('com.button.챗봇', '챗봇'));
  const isDraggable = floating && draggable;

  const handleStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    draggedRef.current = Math.abs(data.x) > DRAG_THRESHOLD || Math.abs(data.y) > DRAG_THRESHOLD;
    if (!draggedRef.current || !nodeRef.current) return;

    const rect = nodeRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const vertical = centerY < window.innerHeight / 2 ? 't' : 'b';
    const horizontal = centerX < window.innerWidth / 2 ? 'l' : 'r';
    const next = `${vertical}${horizontal}` as Corner;

    setCorner(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    onClick?.(e);
  };

  const button = (
    <DockButton
      ref={nodeRef}
      type="button"
      className={`${floating ? `floating ${corner}` : 'inline'} ${isDraggable ? 'draggable' : ''} ${
        className || ''
      }`}
      aria-label={text}
      onClick={handleClick}
      {...props}
    >
      <IconCircle className="icoCircle">
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill={GrayColor.White}
            d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H10l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM7.2 10.5a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0zM10.7 10.5a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0zM14.2 10.5a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0z"
          />
        </svg>
      </IconCircle>
      {!compact && <Label className="label">{text}</Label>}
      <svg
        className="arrow"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke={GrayColor.White}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 8h10M9 4l4 4-4 4" />
      </svg>
    </DockButton>
  );

  if (!isDraggable) return button;

  return (
    /*
      enableUserSelectHack=false — react-draggable의 기본 동작(드래그 시작 시 document.body에
      클래스를 붙이고 `* ::selection` 전역 스타일을 주입)은 문서 전체 스타일을 무효화한다.
      행이 수천인 표가 떠 있는 화면(UT 비교 분석 ④ 등)에서는 그 한 번의 style recalc가 너무
      커서 드래그가 시작되지 않는 것처럼 보인다. 아래 DockButton이 이미 user-select:none이라
      이 hack 없이도 버튼 글자가 선택되지 않는다.
    */
    <Draggable
      nodeRef={nodeRef}
      handle=".icoCircle"
      position={{ x: 0, y: 0 }}
      onStop={handleStop}
      enableUserSelectHack={false}
    >
      {button}
    </Draggable>
  );
};

const IconCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: ${PrimaryColor.Primary800};
  transition: background-color 0.2s;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const DockButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    height: 48px;
    min-width: 0;
    padding: 0 18px 0 6px;
    box-sizing: border-box;
    border: none;
    border-radius: 999px;
    background: ${ControlFilldBG.Primary};
    color: ${GrayColor.White};
    font-family: 'Spoqa Han Sans Neo', 'Malgun Gothic', 'Segoe UI', 'MS YaHei', Sans-Serif;
    line-height: 1;
    box-shadow: 0 2px 8px rgba(31, 119, 149, 0.24);
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s, box-shadow 0.2s;

    &:before {
      display: none;
      content: none;
    }

    &.floating {
      position: fixed;
      z-index: 1200;
      /* 드래그 중 이동이 뒤쪽(큰 표 등)을 다시 그리지 않도록 별도 합성 레이어로 올린다 */
      will-change: transform;
    }

    /* 인라인(툴바) 배치 시 기존 버튼 규격(ButtonSize.sm + labelSM)에 맞춤 */
    &.inline {
      height: 28px;
      gap: 4px;
      padding: 5px 8px;
      border-radius: 2px;
      font-size: 12px;
      box-shadow: none;
      .icoCircle {
        width: 16px;
        height: 16px;
        border-radius: 0;
        background: transparent;
        svg {
          width: 16px;
          height: 16px;
        }
      }
      .label {
        font-size: 12px;
      }
      .arrow {
        display: none;
      }
      &:hover,
      &:active {
        box-shadow: none;
        .icoCircle {
          background: transparent;
        }
      }
    }
    &.br {
      right: 24px;
      bottom: 24px;
    }
    &.bl {
      left: 24px;
      bottom: 24px;
    }
    &.tr {
      right: 24px;
      top: 24px;
    }
    &.tl {
      left: 24px;
      top: 24px;
    }

    /* 아이콘 원만 드래그 핸들: 아이콘은 grab, 나머지는 일반 버튼 포인터 */
    &.draggable .icoCircle {
      cursor: grab;
    }
    &.react-draggable-dragging {
      box-shadow: 0 6px 16px rgba(31, 119, 149, 0.32);
      .icoCircle {
        cursor: grabbing;
      }
    }

    &:hover {
      background: ${ControlFilldBG.PrimaryHover};
      border: none;
      box-shadow: 0 4px 12px rgba(31, 119, 149, 0.28);
      .icoCircle {
        background: ${ControlFilldBG.Primary};
      }
    }

    &:active {
      background: ${ControlFilldBG.PrimaryActive};
      border: none;
      color: ${GrayColor.White};
      box-shadow: 0 1px 4px rgba(31, 119, 149, 0.2);
      .icoCircle {
        background: ${PrimaryColor.Primary900};
      }
    }

    &:focus-visible {
      outline: 2px solid ${PrimaryColor.Primary500};
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
`;
